import { Building2, Landmark, Rocket, Scale, Target } from "lucide-react";
import { SolutionGroupContent } from "./types";

export const government: SolutionGroupContent = {
  key: "government",
  slug: "governo",
  title: "Governo",
  titleKey: "header.solutions_menu.groups.government",
  icon: Landmark,
  tagline: "Serviços públicos mais eficientes, transparentes e seguros, com tecnologia de nuvem e quem entende de gestão pública.",
  description: [
    "Prefeituras, governos estaduais, autarquias e órgãos de controle precisam modernizar sistemas e atender o cidadão com qualidade, sem abrir mão de segurança, conformidade e responsabilidade com o recurso público. A Calriz une tecnologia Google Cloud e experiência no setor público para tirar esses projetos do papel.",
    "Apoiamos desde o planejamento e a contratação, por licitação ou adesão a atas de registro de preços, até a implantação, a operação e a medição de resultados, sempre com aderência à LGPD e às boas práticas de governança.",
  ],
  concepts: ["Cidadão", "Gestão pública", "Dados", "Segurança"],
  items: [
    {
      key: "public_sector",
      slug: "solucoes-para-o-setor-publico",
      title: "Soluções para o Setor Público",
      icon: Building2,
      tagline: "Nuvem, colaboração e dados pensados para a realidade de prefeituras, estados e órgãos públicos.",
      what: [
        "São soluções de infraestrutura, produtividade e dados preparadas para as exigências da administração pública: contratação por processos licitatórios, orçamento anual, auditoria dos órgãos de controle e proteção de dados pessoais do cidadão.",
        "Combinamos Google Cloud, Google Workspace e ferramentas de análise para digitalizar processos internos, integrar secretarias e oferecer serviços on-line ao cidadão, com contas e acessos gerenciados pelo próprio órgão.",
      ],
      concepts: ["Nuvem pública", "Colaboração", "Serviços digitais", "Contratação"],
      benefits: [
        { title: "Contratação simplificada", text: "Apoio técnico na elaboração do termo de referência e na adesão a atas de registro de preços." },
        { title: "Secretarias conectadas", text: "E-mail, documentos e reuniões em um só ambiente, com trabalho integrado entre áreas." },
        { title: "Menos custo de infraestrutura", text: "Troca de servidores locais por nuvem paga pelo uso, sem investimento em hardware." },
        { title: "Transparência e auditoria", text: "Registros de acesso e alteração prontos para os órgãos de controle." },
      ],
      steps: [
        { title: "Diagnóstico", text: "Entendemos os sistemas, contratos e prioridades da gestão para desenhar o escopo." },
        { title: "Apoio à contratação", text: "Especificação técnica, estimativa de custos e documentação para o processo de compra." },
        { title: "Implantação", text: "Configuração do ambiente, migração de dados e integração com os sistemas existentes." },
        { title: "Sustentação", text: "Suporte especializado, formação dos servidores e relatórios de uso e de resultados." },
      ],
      audience: ["Prefeituras", "Governos estaduais", "Autarquias e fundações", "Tribunais e órgãos de controle", "Câmaras e assembleias"],
    },
    {
      key: "governance",
      slug: "governanca-e-conformidade",
      title: "Governança e Conformidade",
      icon: Scale,
      tagline: "Políticas, controles e evidências para que a tecnologia do órgão cumpra a LGPD e as normas de controle.",
      what: [
        "Governança de TI é o conjunto de políticas, papéis e controles que garante que a tecnologia seja usada de forma segura, eficiente e alinhada aos objetivos da instituição. Conformidade é provar, com evidências, que essas regras estão sendo seguidas.",
        "No setor público isso envolve a LGPD, as recomendações dos tribunais de contas e os modelos de segurança da informação do governo. Estruturamos políticas, classificamos dados e configuramos controles de acesso, retenção e auditoria nas ferramentas de nuvem.",
      ],
      concepts: ["Políticas", "LGPD", "Auditoria", "Gestão de riscos"],
      benefits: [
        { title: "Adequação à LGPD", text: "Mapeamento de dados pessoais, bases legais e controles técnicos de proteção." },
        { title: "Prontidão para auditoria", text: "Trilhas de acesso, retenção e relatórios disponíveis quando o controle pedir." },
        { title: "Riscos sob controle", text: "Riscos identificados, priorizados e tratados com plano de ação claro." },
        { title: "Papéis bem definidos", text: "Responsabilidades da TI, do encarregado de dados e das áreas documentadas." },
      ],
      steps: [
        { title: "Avaliação de maturidade", text: "Diagnóstico de políticas, processos e controles frente à LGPD e às boas práticas." },
        { title: "Plano de adequação", text: "Lacunas priorizadas por risco e esforço, com cronograma realista." },
        { title: "Implementação de controles", text: "Políticas de acesso, DLP, retenção e registros configurados na nuvem." },
        { title: "Monitoramento contínuo", text: "Revisões periódicas, indicadores e evidências atualizadas." },
      ],
      audience: ["Órgãos públicos de todas as esferas", "Encarregados de dados (DPO)", "Controladorias e auditorias internas", "Equipes de TI do governo"],
    },
    {
      key: "digital_transformation",
      slug: "transformacao-digital",
      title: "Transformação Digital",
      icon: Rocket,
      tagline: "Processos em papel viram serviços digitais, rápidos para o cidadão e simples para o servidor.",
      what: [
        "Transformação digital no governo é repensar como o serviço público é entregue: tirar processos do papel, integrar sistemas que não conversam e oferecer atendimento on-line, com login único como o gov.br e acompanhamento de cada pedido.",
        "Vai além de comprar tecnologia. Envolve redesenhar fluxos, automatizar etapas repetitivas, usar dados para decidir e preparar os servidores para a nova rotina, com entregas curtas que geram valor desde o início.",
      ],
      concepts: ["Processos", "Automação", "Atendimento on-line", "Dados"],
      benefits: [
        { title: "Atendimento mais rápido", text: "Pedidos on-line, com menos filas e prazos de resposta menores." },
        { title: "Menos papel e retrabalho", text: "Fluxos digitais com assinatura eletrônica e tramitação automática." },
        { title: "Decisões com dados", text: "Painéis com indicadores de cada serviço para orientar a gestão." },
        { title: "Integração entre sistemas", text: "Informações que fluem entre secretarias, sem digitação repetida." },
      ],
      steps: [
        { title: "Mapeamento da jornada", text: "Entendemos o serviço do ponto de vista do cidadão e do servidor." },
        { title: "Priorização", text: "Escolhemos os serviços de maior impacto para começar." },
        { title: "Entregas em ciclos", text: "Digitalização e automação em etapas curtas, testadas com usuários reais." },
        { title: "Adoção e evolução", text: "Treinamento, comunicação e medição para ampliar o que deu certo." },
      ],
      audience: ["Prefeituras e governos estaduais", "Secretarias de gestão e planejamento", "Órgãos de atendimento ao cidadão", "Empresas públicas"],
    },
    {
      key: "high_impact",
      slug: "projetos-de-alto-impacto",
      title: "Projetos de Alto Impacto",
      icon: Target,
      tagline: "Iniciativas de grande escala, com planejamento, execução e resultados à altura do que está em jogo.",
      what: [
        "São projetos que mudam a vida de muitas pessoas ao mesmo tempo: conectar uma rede inteira de escolas, migrar os sistemas críticos de um estado para a nuvem ou criar uma plataforma de dados para políticas públicas. Exigem escala, prazo apertado e zero margem para improviso.",
        "A Calriz coordena esses projetos de ponta a ponta, com escritório de projetos dedicado, equipe técnica certificada e governança com indicadores claros, do planejamento à operação, para que o resultado prometido chegue de fato ao cidadão.",
      ],
      concepts: ["Planejamento", "Escala", "Governança", "Resultados"],
      benefits: [
        { title: "Execução em escala", text: "Capacidade de implantar em centenas de unidades com padrão e qualidade." },
        { title: "Prazos sob controle", text: "Cronograma, marcos e riscos acompanhados por um escritório de projetos." },
        { title: "Resultados medidos", text: "Indicadores definidos no início e reportados durante todo o projeto." },
        { title: "Equipe especializada", text: "Profissionais certificados em nuvem, redes, segurança e educação." },
      ],
      steps: [
        { title: "Planejamento", text: "Escopo, metas, cronograma e plano de riscos validados com a gestão." },
        { title: "Piloto", text: "Implantação controlada para validar a solução antes de escalar." },
        { title: "Implantação em escala", text: "Ondas de execução com equipes em campo e acompanhamento diário." },
        { title: "Operação e avaliação", text: "Sustentação, prestação de contas e medição do impacto." },
      ],
      audience: ["Governos estaduais", "Grandes prefeituras e consórcios", "Redes públicas de ensino", "Órgãos federais"],
    },
  ],
};
